import { Injectable } from '@angular/core'
import { Branding } from './branding.service'

// Get colours from css using css variables
// --- Vareiables are added to the root element in CSS in /styles/app-all.scss ---
// :root { --primary:var(--app-primary);  }

// Get the styles of the root element
// const style = getComputedStyle(document.body);

// Pull out the variables
// const primary = style.getPropertyValue('--color-primary');
// const primaryLight = style.getPropertyValue('--color-primary-light');
// const primaryDark = style.getPropertyValue('--color-primary-dark');
// const secondary = style.getPropertyValue('--color-secondary');
// const secondaryLight = style.getPropertyValue('--color-secondary-light');
// const green = style.getPropertyValue('--color-success-foreground');
// const red = style.getPropertyValue('--color-error-foreground');

export type RGB = {
  r: number
  g: number
  b: number
}

@Injectable({
  providedIn: 'root',
})
export class ColorService {
  public primary = ''
  public secondary = ''
  public accent = ''
  public green = ''
  public red = ''
  public redDark = ''
  public amber = ''
  public locationEdit = ''
  public locationEditLight = ''
  public locationBase = ''
  public locationBaseLight = ''
  public locationBatcher = ''
  public locationBatcherLight = ''
  public locationParking = ''
  public locationParkingLight = ''
  public locationSite = ''
  public locationSiteLight = ''
  public locationRecycling = ''
  public locationRecyclingLight = ''
  public chartColors = [
    '#5B73C1',
    '#9EC97E',
    '#F2C96B',
    '#DE6E6A',
    '#84BEDB',
    '#599F76',
    '#ED8A5D',
    '#9264AF',
    '#DC83C8',
  ]

  constructor() {
    this.loadColorsFromCSSVariables()
  }

  public generateChartColors(count: number): string[] {
    const colors = []
    for (let i = 0; i < count; i++) {
      colors.push(this.chartColors[i % this.chartColors.length])
    }
    return colors
  }

  public interpolateColor(color1: RGB, color2: RGB, factor: number) {
    // Ensure factor is between 0 and 1
    factor = Math.max(0, Math.min(1, factor))

    const r = Math.round(color1.r + factor * (color2.r - color1.r))
    const g = Math.round(color1.g + factor * (color2.g - color1.g))
    const b = Math.round(color1.b + factor * (color2.b - color1.b))

    return `rgb(${r}, ${g}, ${b})`
  }

  public setBranding(branding: Branding) {
    const brandName = branding || 'cloudcycle'
    const root = document.documentElement // Targets :root
    const style = getComputedStyle(document.body) // styles of the body element
    // Pull out the variables
    // this.primary = style.getPropertyValue('--color-primary')

    // CSS variables are defined in app-colors.scss
    // values exist for each branding (currently 'cloudcycle' and 'cidra')
    // e.g.
    // --brand-cloudcycle-color-primary
    // --brand-cidra-color-primary
    // We copy the selected branding values to the used variable names
    // e.g. --color-primary
    const props = [
      'color-primary',
      'color-primary-dark',
      'color-primary-light',
      'color-primary-lightest',
      'color-secondary',
      'color-secondary-light',
      'color-secondary-dark',
      'color-success-foreground',
      'color-success-background',
      'color-success-dark',
    ]
    props.forEach((prop) => {
      // get the brand value
      const brandValue = style.getPropertyValue(`--brand-${brandName}-${prop}`)
      // set the used value
      root.style.setProperty(`--${prop}`, brandValue)
    })
    this.loadColorsFromCSSVariables()
  }

  private loadColorsFromCSSVariables(): void {
    // Get the styles of the root element
    const style = getComputedStyle(document.body)
    // Pull out the variables
    this.primary = style.getPropertyValue('--color-primary')
    this.secondary = style.getPropertyValue('--color-secondary')
    this.green = style.getPropertyValue('--color-success-foreground')
    this.amber = style.getPropertyValue('--color-warning-foreground')
    this.red = style.getPropertyValue('--color-error-foreground')
    this.redDark = style.getPropertyValue('--color-error-dark')

    this.locationEdit = style.getPropertyValue(
      '--color-location-edit-foreground',
    )
    this.locationEditLight = style.getPropertyValue(
      '--color-location-edit-background',
    )
    this.locationBase = style.getPropertyValue(
      '--color-location-base-foreground',
    )
    this.locationBaseLight = style.getPropertyValue(
      '--color-location-base-background',
    )
    this.locationBatcher = style.getPropertyValue(
      '--color-location-batcher-foreground',
    )
    this.locationBatcherLight = style.getPropertyValue(
      '--color-location-batcher-background',
    )
    this.locationSite = style.getPropertyValue(
      '--color-location-site-foreground',
    )
    this.locationSiteLight = style.getPropertyValue(
      '--color-location-site-background',
    )
    this.locationParking = style.getPropertyValue(
      '--color-location-parking-foreground',
    )
    this.locationParkingLight = style.getPropertyValue(
      '--color-location-parking-background',
    )
    this.locationRecycling = style.getPropertyValue(
      '--color-location-recycling-foreground',
    )
    this.locationRecyclingLight = style.getPropertyValue(
      '--color-location-recycling-background',
    )
  }
}
